/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import {
  type AbsoluteURL,
  type APIPermissions,
  ScenarioAPIPermissions,
  ToolAPIPermissions,
  urlMap,
} from '@poweralpha/common';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { usePermissions } from '../../auth';
import { hasFimoConfigAccessPermission } from '../../fimo/permissions';
import { ExpandIconButton } from '../../react-components/buttons';
import Widget from '../../react-components/Widget';

interface URLMapBranch {
  [key: string]: URLMapNode;
}
type URLMapNode = boolean | URLMapBranch;

interface SiteMapLink {
  key: string;
  label: string;
  path: AbsoluteURL;
}

interface SiteMapGroup extends SiteMapLink {
  children: Array<SiteMapLink>;
}

const labelOverrides: Record<string, string> = {
  db: 'Database',
  ess: 'ESS',
  fimo: 'FIMO',
  pv: 'PV',
  pvsim: 'PVSim',
  'bid-requests': 'Bid Requests',
};

const hidePaths = ['/', 'components', 'notifications', 'opportunities', 'projects'];

const formatLabel = (value: string) => {
  const label = labelOverrides[value];
  if (label) return label;
  return value
    .split('-')
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(' ');
};

const normalizePath = (path: string) => {
  if (path === '/') return path;
  return path.endsWith('/') ? path.slice(0, -1) : path;
};

const hasAnyPermission = (
  permissions: Array<APIPermissions> | undefined,
  required: Array<APIPermissions>,
) => required.some((permission) => permissions?.includes(permission));

const canAccessPath = (path: AbsoluteURL, permissions: Array<APIPermissions> | undefined) => {
  const normalizedPath = normalizePath(path);
  const hasDatabaseAccess = hasAnyPermission(permissions, [
    ToolAPIPermissions.WRITE_PLANTSIM,
    ToolAPIPermissions.READ_ESSCOSTING,
    ToolAPIPermissions.READ_PVCOSTING,
    ToolAPIPermissions.READ_HVCOSTING,
    ToolAPIPermissions.READ_PVSIM,
    ScenarioAPIPermissions.WRITE_APPROVAL_ORIGINATION,
  ]);

  switch (normalizedPath) {
    case '/components':
    case '/db':
    case '/db/logs':
      return hasDatabaseAccess;
    case '/db/energy':
      return hasAnyPermission(permissions, [ToolAPIPermissions.WRITE_PLANTSIM]);
    case '/db/hv-costs':
      return hasAnyPermission(permissions, [ToolAPIPermissions.READ_HVCOSTING]);
    case '/db/prices':
      return hasAnyPermission(permissions, [
        ToolAPIPermissions.WRITE_PLANTSIM,
        ScenarioAPIPermissions.WRITE_APPROVAL_ORIGINATION,
      ]);
    case '/db/met-stations':
      return hasAnyPermission(permissions, [
        ToolAPIPermissions.WRITE_PLANTSIM,
        ToolAPIPermissions.READ_PVSIM,
      ]);
    case '/db/weather':
    case '/db/pvmodules':
    case '/db/pvinverters':
      return hasAnyPermission(permissions, [ToolAPIPermissions.READ_PVSIM]);
    case '/db/pv-costs':
      return hasAnyPermission(permissions, [ToolAPIPermissions.READ_PVCOSTING]);
    case '/db/batteries':
    case '/db/essinverters':
    case '/db/mv-transformers':
    case '/db/bess-costs':
      return hasAnyPermission(permissions, [ToolAPIPermissions.READ_ESSCOSTING]);
    case '/db/h2electrolyzers':
    case '/db/h2liquefiers':
    case '/db/thermal-generators':
      return hasAnyPermission(permissions, [
        ToolAPIPermissions.READ_ESSCOSTING,
        ToolAPIPermissions.WRITE_PLANTSIM,
      ]);
    case '/pv':
    case '/pv/logs':
      return hasAnyPermission(permissions, [
        ToolAPIPermissions.READ_PVSIM,
        ToolAPIPermissions.READ_PVCOSTING,
      ]);
    case '/pv/pvsim':
      return hasAnyPermission(permissions, [ToolAPIPermissions.READ_PVSIM]);
    case '/pv/costing':
      return hasAnyPermission(permissions, [ToolAPIPermissions.READ_PVCOSTING]);
    case '/ess/costing':
      return hasAnyPermission(permissions, [ToolAPIPermissions.READ_ESSCOSTING]);
    case '/plant':
      return hasAnyPermission(permissions, [ToolAPIPermissions.WRITE_PLANTSIM]);
    case '/fimo':
    case '/fimo/configurations':
      return hasFimoConfigAccessPermission(permissions);
    default:
      if (normalizedPath.startsWith('/plant/')) {
        return hasAnyPermission(permissions, [ToolAPIPermissions.WRITE_PLANTSIM]);
      }
      return true;
  }
};

const resolveTopLevelPath = (topLevelKey: string): AbsoluteURL => {
  if (topLevelKey === 'components') return '/db';
  if (topLevelKey === 'ess') return '/ess/costing';
  return `/${topLevelKey}` as AbsoluteURL;
};

const resolveSecondLevelPath = (topLevelKey: string, secondLevelKey: string): AbsoluteURL => {
  if (topLevelKey === 'components' && secondLevelKey === 'any') return '/db';
  if (topLevelKey === 'ess' && secondLevelKey === 'costing') return '/ess/costing';
  return `/${topLevelKey}/${secondLevelKey}` as AbsoluteURL;
};

function SiteMap() {
  const permissions = usePermissions();
  const [collapsedByKey, setCollapsedByKey] = useState<Record<string, boolean>>({});

  const groups = useMemo(
    () =>
      Object.entries(urlMap)
        .filter(([topLevelKey]) => !hidePaths.includes(topLevelKey) && !topLevelKey.startsWith(':'))
        .flatMap(([topLevelKey, topLevelValue]) => {
          const topLevelPath = resolveTopLevelPath(topLevelKey);
          const topLevelAllowed = canAccessPath(topLevelPath, permissions);

          const children =
            typeof topLevelValue === 'object'
              ? Object.entries(topLevelValue as Record<string, URLMapNode>)
                  .filter(([secondLevelKey]) => !secondLevelKey.startsWith(':'))
                  .map(([secondLevelKey]) => ({
                    key: `${topLevelKey}/${secondLevelKey}`,
                    label: formatLabel(secondLevelKey),
                    path: resolveSecondLevelPath(topLevelKey, secondLevelKey),
                  }))
                  .filter((child) => canAccessPath(child.path, permissions))
              : [];

          if (!topLevelAllowed && children.length === 0) return [];

          return [
            {
              key: topLevelKey,
              label: formatLabel(topLevelKey),
              path: topLevelPath,
              children,
            } satisfies SiteMapGroup,
          ];
        }),
    [permissions],
  );

  const toggleCollapsed = (groupKey: string) => {
    setCollapsedByKey((previous) => {
      const isCurrentlyCollapsed = previous[groupKey] ?? true;
      return { ...previous, [groupKey]: !isCurrentlyCollapsed };
    });
  };

  return (
    <Widget title="Site Map" showAddButton={false}>
      <Box sx={{ height: 340, overflowY: 'auto' }}>
        {!groups.length ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              px: 1.5,
              py: 2,
            }}
          >
            No pages available for your current permissions.
          </Typography>
        ) : (
          <List dense disablePadding>
            {groups.map((group) => {
              const collapsed = collapsedByKey[group.key] ?? true;

              return (
                <Box key={group.key}>
                  <ListItem disablePadding>
                    <Stack
                      direction="row"
                      sx={{
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      {group.children.length > 0 && (
                        <ExpandIconButton
                          collapsed={collapsed}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            toggleCollapsed(group.key);
                          }}
                          iconSx={{ height: 14, width: 14 }}
                          sx={{ height: 14, width: 14, ml: 1 }}
                        />
                      )}
                      <ListItemButton
                        component={RouterLink}
                        to={group.path}
                        sx={{
                          py: 0,
                          minHeight: 20,
                          px: 1.5,
                          pl: group.children.length > 0 ? 0.5 : 1.5,
                        }}
                      >
                        <ListItemText
                          primary={
                            <MuiLink
                              component="span"
                              underline="always"
                              sx={{
                                display: 'block',
                                fontWeight: 600,
                                fontSize: 13,
                                lineHeight: 1,
                              }}
                            >
                              {group.label}
                            </MuiLink>
                          }
                        />
                      </ListItemButton>
                    </Stack>
                  </ListItem>
                  {group.children.length > 0 && (
                    <Collapse in={!collapsed} timeout="auto" unmountOnExit>
                      <List dense disablePadding>
                        {group.children.map((child) => (
                          <ListItem key={child.key} disablePadding>
                            <ListItemButton
                              component={RouterLink}
                              to={child.path}
                              sx={{ py: 0, minHeight: 20, pl: 3.5, pr: 1.5 }}
                            >
                              <ListItemText
                                primary={
                                  <MuiLink
                                    component="span"
                                    underline="always"
                                    sx={{ display: 'block', fontSize: 13, lineHeight: 1 }}
                                  >
                                    {child.label}
                                  </MuiLink>
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </List>
        )}
      </Box>
    </Widget>
  );
}

export default SiteMap;
